import React, {
  useEffect,
  useMemo,
  useState
} from "react";

const API =
  "https://api.galaxylabsnyc.com/api/v1";

function extractSocialLinks(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.socialLinks)) {
    return payload.socialLinks;
  }

  return [];
}

export default function GalaxyHeaderSettings() {
  const [loading, setLoading] =
    useState(true);

  const [savingBanner, setSavingBanner] =
    useState(false);

  const [savingChat, setSavingChat] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [bannerRecord, setBannerRecord] =
    useState(null);

  const [bannerFile, setBannerFile] =
    useState(null);

  const [settings, setSettings] =
    useState({
      bannerHeadline: "JOIN OUR CHANNEL",
      bannerMessage:
        "EXTRA MENU UPDATES, NEW DROPS & ITEMS NOT LISTED ON THE WEBSITE",
      bannerButtonText: "CLICK HERE →",
      chatEnabled: false,
      chatMode: "telegram",
      chatValue: ""
    });

  const token =
    localStorage.getItem("tokenAdmin");

  async function load() {
    setLoading(true);
    setMessage("");

    try {
      const [
        settingsResponse,
        linksResponse
      ] = await Promise.all([
        fetch(
          API + "/Admin/ui-settings",
          {
            cache: "no-store",
            headers: {
              authorization:
                "Bearer " + token
            }
          }
        ),
        fetch(
          API + "/User/get-social-links",
          {
            cache: "no-store"
          }
        )
      ]);

      const settingsPayload =
        await settingsResponse.json();

      const linksPayload =
        await linksResponse.json();

      if (
        !settingsResponse.ok ||
        settingsPayload?.success !== true
      ) {
        throw new Error(
          settingsPayload?.message ||
          "Could not load website settings."
        );
      }

      setSettings(current => ({
        ...current,
        ...settingsPayload.data
      }));

      const records =
        extractSocialLinks(
          linksPayload
        );

      setBannerRecord(
        records.find(record =>
          record?.name ===
          "Top Website Banner"
        ) ||
        records[0] ||
        null
      );

    } catch (error) {
      setMessage(
        error.message ||
        "Could not load settings."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function saveSettings(
    partial
  ) {
    const response =
      await fetch(
        API + "/Admin/ui-settings",
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",

            authorization:
              "Bearer " + token
          },
          body: JSON.stringify(
            partial
          )
        }
      );

    const payload =
      await response.json();

    if (
      !response.ok ||
      payload?.success !== true
    ) {
      throw new Error(
        payload?.message ||
        "Could not save settings."
      );
    }

    setSettings(current => ({
      ...current,
      ...payload.data
    }));
  }

  async function saveBanner() {
    setSavingBanner(true);
    setMessage("");

    try {
      await saveSettings({
        bannerHeadline:
          settings.bannerHeadline,

        bannerMessage:
          settings.bannerMessage,

        bannerButtonText:
          settings.bannerButtonText
      });

      const form = new FormData();

      form.append(
        "name",
        "Top Website Banner"
      );

      form.append(
        "link",
        bannerRecord?.link || ""
      );

      if (bannerFile) {
        form.append(
          "image",
          bannerFile
        );
      }

      const endpoint =
        bannerRecord?._id
          ? API +
            "/Admin/updateSocialLink/" +
            encodeURIComponent(
              bannerRecord._id
            )
          : API +
            "/Admin/createSocialLink";

      const response =
        await fetch(endpoint, {
          method:
            bannerRecord?._id
              ? "PUT"
              : "POST",

          headers: {
            authorization:
              "Bearer " + token
          },

          body: form
        });

      const payload =
        await response.json();

      if (
        !response.ok ||
        payload?.status === false ||
        payload?.success === false
      ) {
        throw new Error(
          payload?.message ||
          "Could not save banner image/link."
        );
      }

      setBannerFile(null);

      await load();

      setMessage(
        "Top Website Banner saved."
      );

    } catch (error) {
      setMessage(
        error.message ||
        "Banner save failed."
      );
    } finally {
      setSavingBanner(false);
    }
  }

  async function saveChat() {
    setSavingChat(true);
    setMessage("");

    try {
      await saveSettings({
        chatEnabled:
          settings.chatEnabled,

        chatMode:
          settings.chatMode,

        chatValue:
          settings.chatValue
      });

      setMessage(
        "Customer Chat settings saved."
      );

    } catch (error) {
      setMessage(
        error.message ||
        "Chat save failed."
      );
    } finally {
      setSavingChat(false);
    }
  }

  const chatPreview =
    useMemo(() => {
      const value =
        String(
          settings.chatValue || ""
        ).trim();

      if (!value) return "";

      if (
        settings.chatMode ===
        "phone"
      ) {
        return (
          "SMS: " +
          value
        );
      }

      return (
        "Telegram: @" +
        value.replace(/^@/, "")
      );
    }, [
      settings.chatMode,
      settings.chatValue
    ]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          Loading website settings...
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="card mb-4">
        <div className="card-body">
          <h3>
            Top Website Banner
          </h3>

          <p className="text-muted">
            Controls the purple banner
            across the top of the customer website.
          </p>

          <div className="mb-3">
            <label className="form-label">
              Banner headline
            </label>

            <input
              className="form-control"
              value={
                settings.bannerHeadline
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  bannerHeadline:
                    event.target.value
                }))
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Banner message
            </label>

            <input
              className="form-control"
              value={
                settings.bannerMessage
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  bannerMessage:
                    event.target.value
                }))
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Button text
            </label>

            <input
              className="form-control"
              value={
                settings.bannerButtonText
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  bannerButtonText:
                    event.target.value
                }))
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Button destination
            </label>

            <input
              type="url"
              className="form-control"
              value={
                bannerRecord?.link || ""
              }
              onChange={event =>
                setBannerRecord(current => ({
                  ...(current || {}),
                  link:
                    event.target.value
                }))
              }
              placeholder="https://..."
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Banner icon / image
            </label>

            {bannerRecord?.image && (
              <div className="mb-2">
                <img
                  src={
                    bannerRecord.image
                  }
                  alt="Current banner icon"
                  style={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    borderRadius: 10
                  }}
                />
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              className="form-control"
              onChange={event =>
                setBannerFile(
                  event.target.files?.[0] ||
                  null
                )
              }
            />
          </div>

          <button
            type="button"
            className="btn btn-main"
            disabled={savingBanner}
            onClick={saveBanner}
          >
            {savingBanner
              ? "Saving..."
              : "Save Top Banner"}
          </button>
        </div>
      </section>

      <section className="card mb-4">
        <div className="card-body">
          <h3>
            Customer Chat
          </h3>

          <p className="text-muted">
            Controls the purple Chat button
            beside the Cart button.
          </p>

          <div className="form-check form-switch mb-3">
            <input
              id="customer-chat-enabled"
              type="checkbox"
              className="form-check-input"
              checked={
                !!settings.chatEnabled
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  chatEnabled:
                    event.target.checked
                }))
              }
            />

            <label
              htmlFor="customer-chat-enabled"
              className="form-check-label"
            >
              Show Chat button
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Chat destination type
            </label>

            <select
              className="form-select"
              value={
                settings.chatMode
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  chatMode:
                    event.target.value
                }))
              }
            >
              <option value="telegram">
                Telegram Username
              </option>

              <option value="phone">
                Phone Number / SMS
              </option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">
              {settings.chatMode ===
              "telegram"
                ? "Telegram username"
                : "Phone number"}
            </label>

            <input
              className="form-control"
              value={
                settings.chatValue
              }
              onChange={event =>
                setSettings(current => ({
                  ...current,
                  chatValue:
                    event.target.value
                }))
              }
              placeholder={
                settings.chatMode ===
                "telegram"
                  ? "@yourusername"
                  : "+1 555 555 5555"
              }
            />

            {chatPreview && (
              <small className="text-muted">
                Opens: {chatPreview}
              </small>
            )}
          </div>

          <button
            type="button"
            className="btn btn-main"
            disabled={savingChat}
            onClick={saveChat}
          >
            {savingChat
              ? "Saving..."
              : "Save Customer Chat"}
          </button>
        </div>
      </section>

      

      {message && (
        <div
          className="alert alert-info"
          role="status"
        >
          {message}
        </div>
      )}
    </div>
  );
}
